import React, { useEffect, useRef, useState, useCallback, useContext } from "react";
import { ThemeContext } from "../App";
import {
  Printer, Copy, Volume2, Square, X, ArrowUp, Clock, Users, Timer, Heart, Share2,
  ShoppingBasket, ChefHat, MapPin, Tag,
} from "lucide-react";
import Swal from "sweetalert2";
import printRecipe_ from "./printRecipe";
import CookingMode from "./cooking/CookingMode";
import { StarRating } from "./common/Common";
import { fetchRecipeDetails, fetchSimilar } from "../api/recipes";
import { formatMeasure } from "../utils/units";
import {
  getIngredients, getRecipeId, getRecipeImage, getRecipeTitle, getSteps, isSpoonacular, toSummary,
} from "../utils/recipe";
import { useFavorites } from "../utils/favorites";
import { readJSON, writeJSON } from "../utils/storage";
import { useRecentlyViewed, useRecipeNotes, useShoppingList } from "../utils/stores";
import "./Modal.css";

const EXCLUDED_WORDS = ["beef"];

// Centered confirmation popup (like the other "Added!" messages in the app)
const toast = (icon, title, text) =>
  Swal.fire({ icon, title, text, showConfirmButton: false, timer: 1800 });

const UNIT_OPTIONS = [
  { value: "original", label: "Original" },
  { value: "metric", label: "Metric" },
  { value: "imperial", label: "Imperial" },
];

const Modal = ({ isOpen, onClose, recipe: recipeProp }) => {
  const { theme } = useContext(ThemeContext);
  const scrollRef = useRef(null);
  const timerIds = useRef({});
  const cookOpenRef = useRef(false);

  const [override, setOverride] = useState(null);
  const [checked, setChecked] = useState({});
  const [servings, setServings] = useState(1);
  const [timers, setTimers] = useState({});
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [unitSystem, setUnitSystem] = useState("original");
  const [cookOpen, setCookOpen] = useState(false);
  const [similar, setSimilar] = useState([]);
  const [openingId, setOpeningId] = useState(null);

  const { isFavorite, addFavorite, removeFavorite } = useFavorites();
  const shopping = useShoppingList();
  const { get: getNote, setNote } = useRecipeNotes();
  const { push: pushRecent } = useRecentlyViewed();

  const recipe = override || recipeProp;
  cookOpenRef.current = cookOpen;

  // A new recipe from the page replaces any "similar" recipe we navigated to
  useEffect(() => {
    setOverride(null);
  }, [recipeProp]);

  // ESC + scroll lock
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && !cookOpenRef.current && onClose();
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", esc);
    }
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", esc);
    };
  }, [isOpen, onClose]);

  // Load saved state
  useEffect(() => {
    if (!recipe) return;
    const id = getRecipeId(recipe);
    const saved = readJSON(`recipe_${id}`, {});
    setChecked(saved.checked || {});
    setServings(recipe.servings || 1);
    setUnitSystem("original");
    setCookOpen(false);
    scrollRef.current?.scrollTo({ top: 0 });
  }, [recipe]);

  // Recently viewed + similar recipes
  useEffect(() => {
    if (!isOpen || !recipe) return undefined;
    pushRecent(toSummary(recipe));
    let cancelled = false;
    setSimilar([]);
    fetchSimilar(recipe, EXCLUDED_WORDS)
      .then((list) => !cancelled && setSimilar(list))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isOpen, recipe, pushRecent]);

  // Stop timers + speech when the modal closes / unmounts
  useEffect(() => {
    const ids = timerIds.current;
    return () => {
      Object.values(ids).forEach(clearInterval);
      window.speechSynthesis?.cancel();
    };
  }, []);
  useEffect(() => {
    if (!isOpen) window.speechSynthesis?.cancel();
  }, [isOpen]);

  // Persist checkboxes
  const toggleIngredient = useCallback(
    (ingId) => {
      if (!recipe) return;
      const newChecked = { ...checked, [ingId]: !checked[ingId] };
      setChecked(newChecked);
      writeJSON(`recipe_${getRecipeId(recipe)}`, { checked: newChecked });
    },
    [checked, recipe]
  );

  // Voice Read-Aloud
  const speak = useCallback((text, onEnd) => {
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
      const utter = new SpeechSynthesisUtterance(text);
      utter.rate = 0.9;
      utter.pitch = 1;
      utter.onend = () => {
        setIsSpeaking(false);
        onEnd?.();
      };
      utter.onerror = () => setIsSpeaking(false);
      setIsSpeaking(true);
      window.speechSynthesis.speak(utter);
    }
  }, []);

  const stopSpeaking = useCallback(() => {
    window.speechSynthesis?.cancel();
    setIsSpeaking(false);
  }, []);

  if (!isOpen || !recipe) {
    return null;
  }

  const spoon = isSpoonacular(recipe);
  const id = getRecipeId(recipe);
  const recipeTitle = getRecipeTitle(recipe);
  const recipeImage = getRecipeImage(recipe);
  const ingredients = getIngredients(recipe);
  const steps = getSteps(recipe);
  const note = getNote(id);
  const saved = isFavorite(id);

  const ratio = spoon && recipe.servings ? servings / recipe.servings : 1;
  const display = (ing) => formatMeasure(ing.original, { ratio, system: unitSystem });
  const displayList = ingredients.map(display);

  // Timer logic
  const startTimer = (stepIndex, minutes) => {
    clearInterval(timerIds.current[stepIndex]);
    setTimers((prev) => ({ ...prev, [stepIndex]: Math.round(minutes * 60) }));
    timerIds.current[stepIndex] = setInterval(() => {
      setTimers((t) => {
        const remaining = (t[stepIndex] || 0) - 1;
        if (remaining <= 0) {
          clearInterval(timerIds.current[stepIndex]);
          return { ...t, [stepIndex]: 0 };
        }
        return { ...t, [stepIndex]: remaining };
      });
    }, 1000);
  };

  const formatTime = (sec) =>
    sec > 0 ? `${Math.floor(sec / 60)}:${(sec % 60).toString().padStart(2, "0")}` : "Start";

  const toggleFavorite = () => {
    if (saved) {
      removeFavorite(id);
      toast("success", "Removed!", "This recipe has been removed from your favorites.");
      return;
    }
    addFavorite({
      ...toSummary(recipe),
      ...(spoon
        ? { cuisines: recipe.cuisines, dishTypes: recipe.dishTypes, diets: recipe.diets }
        : { strCategory: recipe.strCategory, strArea: recipe.strArea }),
    });
    toast("success", "Added!", "This recipe has been added to your favorites.");
  };

  const shareRecipe = async () => {
    const url = `${window.location.origin}/?recipe=${id}&source=${spoon ? "spoonacular" : "themealdb"}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: recipeTitle, text: `Check out ${recipeTitle} on Recify`, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast("success", "Copied!", "The recipe link has been copied to your clipboard.");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast("error", "Could not share this recipe");
    }
  };

  const addToShoppingList = () => {
    const added = shopping.addMany(id, recipeTitle, displayList);
    if (added) {
      toast("success", "Added!", `${added} item${added > 1 ? "s" : ""} added to your shopping list.`);
    } else {
      toast("info", "Already added", "These ingredients are already on your shopping list.");
    }
  };

  const print = () => {
    const meta = spoon
      ? [
          { label: "Ready in", value: recipe.readyInMinutes ? `${recipe.readyInMinutes} min` : "" },
          { label: "Servings", value: String(servings) },
        ]
      : [
          { label: "Category", value: recipe.strCategory },
          { label: "Cuisine", value: recipe.strArea },
        ];
    const tags = spoon
      ? [...(recipe.dishTypes || []), ...(recipe.diets || [])].slice(0, 8)
      : (recipe.strTags || "").split(",").map((t) => t.trim()).filter(Boolean);

    printRecipe_({
      title: recipeTitle,
      image: recipeImage,
      meta,
      tags,
      ingredients: displayList.map((t) => t.trim()),
      steps: steps.map((s) => ({ text: s.text, minutes: s.minutes })),
    });
  };

  // Copy ingredients
  const copyIngredients = () => {
    const list = displayList.map((i) => `- ${i}`).join("\n");
    navigator.clipboard.writeText(`${recipeTitle}\n\nIngredients:\n${list}`);
    toast("success", "Copied!", "The ingredients have been copied to your clipboard.");
  };

  const readFullRecipe = () => {
    const instructionsText = steps.map((s) => s.text).join(" ");
    speak(`${recipeTitle}. Ingredients: ${displayList.join(". ")}. Instructions: ${instructionsText}`);
  };

  const openSimilar = async (item) => {
    setOpeningId(item.id);
    try {
      const data = await fetchRecipeDetails(item);
      setOverride(data);
    } catch {
      toast("error", "Couldn't open that recipe");
    } finally {
      setOpeningId(null);
    }
  };

  const renderInstructions = () => (
    <ol className="instructions-list">
      {steps.map((step, idx) => {
        const timerKey = `step-${idx}`;
        const remaining = timers[timerKey];
        return (
          <li key={idx} className="instruction-step">
            {step.html ? (
              <div className="step-content" dangerouslySetInnerHTML={{ __html: step.html }} />
            ) : (
              <div className="step-content">{step.text}</div>
            )}
            <div className="step-actions">
              {step.minutes && (
                <button className="timer-btn" onClick={() => startTimer(timerKey, step.minutes)}>
                  <Timer size={13} /> {remaining > 0 ? formatTime(remaining) : `${step.minutes} min`}
                </button>
              )}
              <button className="voice-btn" onClick={() => speak(step.text)} disabled={isSpeaking}>
                {isSpeaking ? "..." : "Read"}
              </button>
            </div>
          </li>
        );
      })}
    </ol>
  );

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={recipeTitle}
        className={`modal-overlay ${theme ? "dark" : ""}`}
        onClick={onClose}
      >
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>

          <div className="modal-body" ref={scrollRef}>
            <div className="modal-printable">
              <img src={recipeImage} alt={recipeTitle} className="modal-hero" />
              <h2 className="modal-title">{recipeTitle}</h2>

              <div className="modal-stats">
                {spoon ? (
                  <>
                    <span><Clock size={16} /> <strong>Ready in:</strong> {recipe.readyInMinutes ?? "-"} min</span>
                    <span>
                      <Users size={16} /> <strong>Servings:</strong>
                      <input
                        type="number"
                        min="1"
                        value={servings}
                        onChange={(e) => setServings(Math.max(1, parseInt(e.target.value) || 1))}
                        className="servings-input"
                      />
                    </span>
                  </>
                ) : (
                  <>
                    {recipe.strCategory && <span><Tag size={16} /> <strong>{recipe.strCategory}</strong></span>}
                    {recipe.strArea && <span><MapPin size={16} /> <strong>{recipe.strArea}</strong></span>}
                  </>
                )}
                <span className="modal-rating">
                  <StarRating value={note.rating} onChange={(v) => setNote(id, { rating: v })} label="Your rating" size={18} />
                </span>
              </div>

              <div className="modal-actions">
                <button
                  className={`modal-action-btn fav ${saved ? "saved" : ""}`}
                  onClick={toggleFavorite}
                  aria-pressed={saved}
                >
                  <Heart size={16} fill={saved ? "currentColor" : "none"} /> {saved ? "Saved" : "Favorite"}
                </button>
                {steps.length > 0 && (
                  <button className="modal-action-btn start-cook" onClick={() => setCookOpen(true)}>
                    <ChefHat size={16} /> Start cooking
                  </button>
                )}
                <button className="modal-action-btn shop" onClick={addToShoppingList}>
                  <ShoppingBasket size={16} /> Shopping list
                </button>
                <button className="modal-action-btn share" onClick={shareRecipe}>
                  <Share2 size={16} /> Share
                </button>
                <button className="modal-action-btn print" onClick={print}>
                  <Printer size={16} /> Print
                </button>
                <button className="modal-action-btn copy" onClick={copyIngredients}>
                  <Copy size={16} /> Copy Ingredients
                </button>
                <button className="modal-action-btn voice" onClick={readFullRecipe} disabled={isSpeaking}>
                  <Volume2 size={16} /> {isSpeaking ? "..." : "Read Full"}
                </button>
                {isSpeaking && (
                  <button className="modal-action-btn stop" onClick={stopSpeaking}>
                    <Square size={14} /> Stop
                  </button>
                )}
              </div>

              <div className="modal-columns">
                <section className="modal-section">
                  <div className="modal-section-head">
                    <h4 className="modal-section-title">Ingredients</h4>
                    <div className="unit-toggle" role="group" aria-label="Units">
                      {UNIT_OPTIONS.map((o) => (
                        <button
                          key={o.value}
                          className={unitSystem === o.value ? "active" : ""}
                          aria-pressed={unitSystem === o.value}
                          onClick={() => setUnitSystem(o.value)}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <ul className="modal-list">
                    {ingredients.map((ing, i) => {
                      const isChecked = !!checked[ing.id];
                      return (
                        <li
                          key={ing.id}
                          className={`ingredient-item ${isChecked ? "checked" : ""}`}
                          onClick={() => toggleIngredient(ing.id)}
                        >
                          <input type="checkbox" checked={isChecked} readOnly className="ingredient-checkbox" />
                          <span>{displayList[i]}</span>
                        </li>
                      );
                    })}
                  </ul>
                </section>

                <section className="modal-section">
                  <h4 className="modal-section-title">Instructions</h4>
                  <div className="modal-instructions">{renderInstructions()}</div>
                </section>
              </div>

              <section className="modal-notes">
                <h4 className="modal-section-title">My notes</h4>
                <textarea
                  className="notes-input"
                  rows={3}
                  aria-label="My notes"
                  placeholder="Tweaks, substitutions, what the family thought…"
                  value={note.note}
                  onChange={(e) => setNote(id, { note: e.target.value })}
                />
              </section>

              {similar.length > 0 && (
                <section className="modal-similar">
                  <h4 className="modal-section-title">You might also like</h4>
                  <div className="similar-row">
                    {similar.map((s) => (
                      <button
                        key={s.id}
                        className={`similar-card ${openingId === s.id ? "loading" : ""}`}
                        onClick={() => openSimilar(s)}
                        disabled={openingId !== null}
                      >
                        <img src={s.image} alt="" loading="lazy" />
                        <span>{s.title}</span>
                      </button>
                    ))}
                  </div>
                </section>
              )}
            </div>

            <button
              className="modal-top-btn"
              aria-label="Back to top"
              onClick={() => scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" })}
            >
              <ArrowUp size={18} />
            </button>
          </div>
        </div>
      </div>

      <CookingMode
        open={cookOpen}
        onClose={() => setCookOpen(false)}
        title={recipeTitle}
        steps={steps}
        ingredients={displayList}
        onSpeak={speak}
        onStopSpeak={stopSpeaking}
      />
    </>
  );
};

export default Modal;
